import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { RootState } from "@/store/RootStore"
import { make } from "vuex-pathify"
import { StoreUtils } from "@/utils/StoreUtils"
import Services from "@/services/Services"
import { GuestBookingDetails } from "@/models/BookingEvent"

export const namespaced: boolean = true

export interface GuestWaiverState {
    bookingDetails?: GuestBookingDetails
    consentGiven: boolean
    submitted: boolean
    invitationAlreadyConfirmed: boolean
}

function defaultState(): GuestWaiverState {
    return {
        bookingDetails: undefined,
        consentGiven: false,
        submitted: false,
        invitationAlreadyConfirmed: false
    }
}

export const state: GuestWaiverState = defaultState()

//===================================
// Getters
//===================================
export const getters: GetterTree<GuestWaiverState, RootState> = {}

//===================================
// Actions
//===================================
export const actions: ActionTree<GuestWaiverState, RootState> = {
    ...make.actions(state),

    resetState({ commit }) {
        commit("resetState")
    },

    async fetchGuestBooking({ dispatch }, hash: string) {
        await StoreUtils.withLoading(
            dispatch,
            async () => {
                const bookingDetails = await Services.guestWaiver.getGuestBooking(hash)
                dispatch("setBookingDetails", bookingDetails)
            },
            async (err) => {
                const message: string = err?.response?.data?.errorMessage || err?.response?.data?.message || err?.message || ""
                if (message.toLowerCase().includes("already been confirmed") || err?.response?.status === 409) {
                    dispatch("setInvitationAlreadyConfirmed", true)
                    return true // Suppress default error toast — show friendly UI instead
                }
                return false
            }
        )
    },

    async confirmGuest({ dispatch, state }, hash: string) {
        await StoreUtils.withLoading(dispatch, async () => {
            const details = state.bookingDetails
            if (!details) throw new Error("Booking details not found")
            await Services.guestWaiver.confirmGuest(hash, details)
            dispatch("setSubmitted", true)
        })
    }
}

//===================================
// Mutations
//===================================
export const mutations: MutationTree<GuestWaiverState> = {
    ...make.mutations(state),

    resetState(currentState: GuestWaiverState) {
        Object.assign(currentState, defaultState())
    }
}

export const guestWaiver: Module<GuestWaiverState, RootState> = {
    namespaced,
    state,
    getters,
    actions,
    mutations
}
